import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControlLabel,
  Radio,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Container,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  AccessTime as TimeIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { decryptAppId, decryptText, encryptAppId, encryptText } from "../../../utils/encryption";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import s3Utils from '../../../utils/s3Utils';
import PageTitle from "../../../components/PageTitle";
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';

// Schedule options for assessor activities
const SCHEDULE_OPTIONS = [
  'Opening meeting',
  'Lab Round (Testing, Reception & phlebotomy and Storage areas)',
  'Assess Technical requirements',
  'Assess Management requirements',
  'Review of documents',
  'Testing & retesting',
  'Assess SCF',
  'Debriefing with Assessment team & CAB',
  'Compilation & preparation of Assessment Report',
  'Closing meeting'
];

const SCHEDULE_OPTIONS_TESTING_CALIBRATION = [
  'Opening meeting',
  'Lab Round (Testing, Reception and Storage areas)',
  'Assess Technical requirements',
  'Assess Management requirements',
  'Review of documents',
  'Testing & retesting',
  'Debriefing with Assessment team & CAB',
  'Compilation & preparation of Assessment Report',
  'Closing meeting'
];

const NAF1Form = () => {
  const location = useLocation();

  const getDecryptedParam = (paramName) => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedParam = searchParams.get(paramName);
    if (encryptedParam) {
      try {
        return decryptText(encryptedParam);
      } catch (error) {
        return encryptedParam;
      }
    }
    return null;
  };
  const getDecryptedAppParam = (paramName) => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedParam = searchParams.get(paramName);
    if (encryptedParam) {
      try {
        return decryptAppId(encryptedParam);
      } catch (error) {
        return encryptedParam;
      }
    }
    return null;
  };

  const [orgId, setOrgId] = useState(() => getDecryptedParam("orgId"));
  const [appId, setAppId] = useState(() => getDecryptedAppParam("appId"));
  const [assessmentId, setAssessmentId] = useState(() => getDecryptedParam("assessmentId"));
  const [assessorId, setAssessorId] = useState(() => getDecryptedParam("assessorId"));
  const [labTypeId, setLabTypeId] = useState(() => getDecryptedParam("labTypeId"));

  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });

  const [loadingAssessors, setLoadingAssessors] = useState(false);
  const [assessmentScheduleId, setAssessmentScheduleId] = useState("");
  const [assessorScheduleIdExist, setAssessorScheduleIdExist] = useState(false);
  const [assessors, setAssessors] = useState([]);
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    laboratoryName: "",
    authorizedRepresentative: "",
    visitDates: "",
    assessmentType: "",
    field: "",
    discipline: "",
    group:"",
    facilityType: "",
    assessmentStandard: "",
    morningStart: "",
    morningEnd: "",
    afternoonStart: "",
    afternoonEnd: "",
    openingMeetingDate: "",
    openingMeetingTime: "",
    closingMeetingDate: "",
    closingMeetingTime: "",
    day1DebriefingDate: "",
    day1DebriefingTime: "",
    day2DebriefingDate: "",
    day2DebriefingTime: "",
    naf1File: null,
    declarationSigned: false,
  });

  const updateAssessorSchedule = (index, field, value) => {
    console.log('🔄 Updating assessor at index:', { index, field, value });
    setAssessors(prevAssessors => {
      const updated = prevAssessors.map((assessor, idx) => {
        if (idx === index) {
          console.log('✅ Match found at index:', index);
          return { ...assessor, [field]: value };
        }
        return assessor;
      });
      console.log('📋 Updated assessors:', updated);
      return updated;
    });
  };

  const fetchAssessorSchedule = async () => {
    if (!appId || !assessmentId) return;

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAssessmentScheduleAssessor?appid=${appId}&assessmentid=${assessmentId}`;
      const response = await axios.get(url);

      if (response.data && response.data.isstatus && Array.isArray(response.data.assessor)) {
        console.log('📥 Received schedule data:', response.data.assessor);

        // Check if any assessor has a valid schedule ID (non-empty and not null)
        const hasValidScheduleId = response.data.assessor.some(
          scheduleData => scheduleData.id && scheduleData.id !== "" && scheduleData.id !== null
        );
        if (hasValidScheduleId) {
          setAssessorScheduleIdExist(true);
          console.log('✅ Assessor schedule ID exists');
        } else {
          setAssessorScheduleIdExist(false);
          console.log('⚠️ No valid assessor schedule ID found');
        }

        // Check if we have existing assessors or need to create from API response
        setAssessors(prevAssessors => {
          // If no existing assessors, use the data from API
          if (prevAssessors.length === 0 && response.data.assessor.length > 0) {
            console.log('📋 Creating assessors from schedule API');
            return response.data.assessor.map((scheduleData, index) => ({
              id: scheduleData.id || "",
              assessorId: scheduleData.assessorId || "",
              discipline: scheduleData.discipline || "",
              group: scheduleData.group || "",
              assessorName: scheduleData.assessorName || "Unknown Assessor",
              name: scheduleData.assessorName || "Unknown Assessor",
              role: scheduleData.capacity || "Assessor",
              scheduleId: scheduleData.id || "",
              day1Morning: scheduleData.morning1 ? scheduleData.morning1.split('|').filter(Boolean) : [],
              day1Afternoon: scheduleData.afternoon1 ? scheduleData.afternoon1.split('|').filter(Boolean) : [],
              day2Morning: scheduleData.morning2 ? scheduleData.morning2.split('|').filter(Boolean) : [],
              day2Afternoon: scheduleData.afternoon2 ? scheduleData.afternoon2.split('|').filter(Boolean) : [],
            }));
          }

          // Otherwise, merge with existing assessors
          return prevAssessors.map(assessor => {
            const scheduleData = response.data.assessor.find(
              s => String(s.assessorId) === String(assessor.assessorId) ||
                String(s.assessorId) === String(assessor.id) ||
                s.assessorName === assessor.name
            );

            if (scheduleData) {
              return {
                ...assessor,
                scheduleId: scheduleData.id || "",
                day1Morning: scheduleData.morning1 ? scheduleData.morning1.split('|').filter(Boolean) : [],
                day1Afternoon: scheduleData.afternoon1 ? scheduleData.afternoon1.split('|').filter(Boolean) : [],
                day2Morning: scheduleData.morning2 ? scheduleData.morning2.split('|').filter(Boolean) : [],
                day2Afternoon: scheduleData.afternoon2 ? scheduleData.afternoon2.split('|').filter(Boolean) : [],
              };
            }
            return assessor;
          });
        });
        console.log('✅ Loaded assessor schedule');
      }
    } catch (error) {
      console.error('Error fetching assessor schedule:', error);
      // Don't show error to user as this might be first time
    }
  };

  const saveAssessorSchedule = async () => {
    if (!appId || !assessmentId || !orgId) {
      showSnackbar('Missing required parameters', 'error');
      return;
    }

    try {
      setLoading(true);

      // Save/Update each assessor's schedule
      const promises = assessors.map(async (assessor) => {
        const payload = {
          appId: String(appId),
          orgId: String(orgId),
          assessorId: String(assessor.assessorId || assessor.id),
          assessmentId: String(assessmentId),
          morning1: Array.isArray(assessor.day1Morning) ? assessor.day1Morning.join('|') : (assessor.day1Morning || ""),
          afternoon1: Array.isArray(assessor.day1Afternoon) ? assessor.day1Afternoon.join('|') : (assessor.day1Afternoon || ""),
          morning2: Array.isArray(assessor.day2Morning) ? assessor.day2Morning.join('|') : (assessor.day2Morning || ""),
          afternoon2: Array.isArray(assessor.day2Afternoon) ? assessor.day2Afternoon.join('|') : (assessor.day2Afternoon || ""),
          insertedby: assessorId ? String(assessorId) : "0",
        };

        // If schedule ID exists, update; otherwise, create new
        if (assessor.scheduleId) {
          payload.id = String(assessor.scheduleId);
          const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessmentScheduleAssessor`;
          return axios.put(url, payload);
        } else {
          const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveAssessmentScheduleAssessor`;
          return axios.post(url, payload);
        }
      });

      await Promise.all(promises);
      showSnackbar('Assessor schedule saved successfully!', 'success');

      // Refresh schedule data
      await fetchAssessorSchedule();
    } catch (error) {
      console.error('Error saving assessor schedule:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Error saving assessor schedule',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appId && assessmentId) {
      fetchAssessorSchedule();
      fetchAssessmentSchedule();
    }
  }, [appId, assessmentId]);

  // Fetch main Assessment Schedule data
  const fetchAssessmentSchedule = async () => {
    if (!appId || !assessmentId || !orgId || !assessorId) return;

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAssessmentSchedule?appid=${appId}&assessmentid=${assessmentId}&orgid=${orgId}&assessorid=${assessorId}`;
      const response = await axios.get(url);

      if (response.data && response.data.isstatus) {
        const data = response.data;
        console.log('📥 Received assessment schedule data:', data);

        // Store the schedule ID for updates
        setAssessmentScheduleId(data.id || "");

        // Populate form data from API response
        setFormData(prev => ({
          ...prev,
          laboratoryName: data.labName || prev.laboratoryName,
          authorizedRepresentative: data.authLab || prev.authorizedRepresentative,
          visitDates: data.dateofVisit || prev.visitDates,
          field: data.field || prev.field,
          discipline: data.disciplines || prev.discipline,
          group: data.groups || prev.group,
          facilityType: data.facility || prev.facilityType,
          assessmentStandard: data.standard ? (data.field === 'Medical' ? 'ISO 15189:' + data.standard : 'ISO/IEC 17025:' + data.standard) : prev.assessmentStandard,
          assessmentType: data.assType || '-',
          morningStart: data.assessment_Time_Mor || '',
          afternoonStart: data.assessment_Time_Aft || '',
          openingMeetingDate: data.opening_Meeting || prev.openingMeetingDate,
          closingMeetingDate: data.closing_Meeting || prev.closingMeetingDate,
          day1DebriefingDate: data.daily_Debriefing1 || prev.day1DebriefingDate,
          day2DebriefingDate: data.daily_Debriefing2 || prev.day2DebriefingDate,
        }));

        console.log('✅ Loaded assessment schedule data');
      }
    } catch (error) {
      console.error('Error fetching assessment schedule:', error);
      // Don't show error to user as this might be first time
    }
  };

  // Save/Update main Assessment Schedule
  const saveAssessmentSchedule = async () => {
    if (!appId || !assessmentId || !orgId || !assessorId) {
      throw new Error('Missing required parameters');
    }

    try {
      const payload = {
        appId: String(appId),
        orgId: String(orgId),
        assessorId: String(assessorId),
        assessmentId: String(assessmentId),
        assessment_Time_Mor: formData.morningStart,
        assessment_Time_Aft: formData.afternoonStart,
        opening_Meeting: formData.openingMeetingDate || "",
        closing_Meeting: formData.closingMeetingDate || "",
        daily_Debriefing1: formData.day1DebriefingDate || "",
        daily_Debriefing2: formData.day2DebriefingDate || "",
        insertedby: String(assessorId),
      };

      let response;
      // If schedule ID exists, update; otherwise, create new
      if (assessmentScheduleId) {
        payload.id = String(assessmentScheduleId);
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessmentSchedule`;
        response = await axios.put(url, payload);
        console.log('✅ Updated assessment schedule');
        showSnackbar('Assessment schedule updated successfully!', 'success');
        await fetchAssessmentSchedule();
      } else {
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveAssessmentSchedule`;
        response = await axios.post(url, payload);
        console.log('✅ Saved new assessment schedule');
        showSnackbar('Assessment schedule saved successfully!', 'success');
        await fetchAssessmentSchedule();
      }
      return response;
    } catch (error) {
      console.error('Error saving assessment schedule:', error);
      throw error;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveAssessmentSchedule();
  };

  const resetForm = () => {
    setFormData({
      laboratoryName: "",
      authorizedRepresentative: "",
      visitDates: "",
      assessmentType: "",
      field: "",
      discipline: "",
      facilityType: "",
      assessmentStandard: "",
      morningStart: "",
      morningEnd: "",
      afternoonStart: "",
      afternoonEnd: "",
      openingMeetingDate: "",
      openingMeetingTime: "",
      closingMeetingDate: "",
      closingMeetingTime: "",
      day1DebriefingDate: "",
      day2DebriefingDate: "",
      day2DebriefingTime: "",
      naf1File: null,
      declarationSigned: false,
    });
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const generatePDF = (returnBlob = false) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    // Header with border and gradient background
    doc.setFillColor(99, 102, 241); // Modern indigo color (#6366f1)
    doc.rect(0, 0, pageWidth, 20, 'F');

    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("ASSESSMENT SCHEDULE", pageWidth / 2, 12, { align: 'center' });

    // Reset text color
    doc.setTextColor(0, 0, 0);
    yPos = 35;

    // Laboratory Information Section
    doc.setFillColor(243, 244, 246); // Light gray background (#f3f4f6)
    doc.rect(14, yPos - 5, pageWidth - 28, 10, 'F');
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(99, 102, 241); // Indigo text (#6366f1)
    doc.text("Laboratory Information", 16, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 8;

    // Laboratory Information Table
    autoTable(doc, {
      startY: yPos,
      theme: 'striped',
      headStyles: {
        fillColor: [99, 102, 241], // Indigo (#6366f1)
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: 3
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [229, 231, 235], // Gray border (#e5e7eb)
        lineWidth: 0.1
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251] // Very light gray (#f9fafb)
      },
      body: [
        ['Name of the Laboratory', formData.laboratoryName || '-'],
        ['Authorized Representative', formData.authorizedRepresentative || '-'],
        ['Date(s) of Visit', formData.visitDates || '-'],
        ['Type of Assessment', formData.assessmentType || '-'],
        ['Field', formData.field || '-'],
        ['Discipline(s)', formData.discipline || '-'],
        ...(
        formData.field === 'Testing' || formData.field === 'Calibration'
        ? [['Group(s)', formData.group || '-']]
        : []
        ),
        ['Facility Type', formData.facilityType || '-'],
        ['Assessment Standard', `${formData.assessmentStandard}\nSpecific Criteria of NABL (NABL 112A or NABL 135, as applicable)` || '-']
      ],
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold', fillColor: [243, 244, 246] }, // Light gray (#f3f4f6)
        1: { cellWidth: 125 }
      }
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // Assessment Schedule Section Header
    doc.setFillColor(243, 244, 246); // Light gray (#f3f4f6)
    doc.rect(14, yPos - 5, pageWidth - 28, 10, 'F');
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(99, 102, 241); // Indigo (#6366f1)
    doc.text("Assessment Schedule & Timings", 16, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 8;

    // Assessment Timings Table
    autoTable(doc, {
      startY: yPos,
      theme: 'grid',
      headStyles: {
        fillColor: [99, 102, 241], // Indigo (#6366f1)
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
        cellPadding: 3
      },
      bodyStyles: {
        fontSize: 8.5,
        cellPadding: 4,
        valign: 'top'
      },
      head: [[
        'Daily Timings',
        'Opening/Closing Meeting',
        'Daily Debriefings'
      ]],
      body: [[
        `Morning: ${formData.morningStart || '-'}\n\nAfternoon: ${formData.afternoonStart || '-'}`,
        `Opening Meeting: ${formatDateTime(formData.openingMeetingDate) || '-'}\n\nClosing Meeting: ${formatDateTime(formData.closingMeetingDate) || '-'}`,
        `Day 1: ${formatDateTime(formData.day1DebriefingDate) || '-'}\n\nDay 2: ${formatDateTime(formData.day2DebriefingDate) || '-'}`
      ]],
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { cellWidth: 60, halign: 'left' },
        1: { cellWidth: 62, halign: 'left' },
        2: { cellWidth: 63, halign: 'left' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // Assessor Schedule Section Header
    doc.setFillColor(243, 244, 246); // Light gray (#f3f4f6)
    doc.rect(14, yPos - 5, pageWidth - 28, 10, 'F');
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(99, 102, 241); // Indigo (#6366f1)
    doc.text("Assessor Schedule", 16, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 3;

    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.text("Schedule of Department/Section/Activity to be Assessed (date wise)", 16, yPos);
    yPos += 5;

    console.log('assessor Data', assessors)

    // Prepare assessor data
    const hasLeadAssessor = assessors.some(
      a => (a.role?.toLowerCase() === "la" || a.role?.toLowerCase() === "lata")
    );

    const assessorRows = assessors.map((assessor, index) => {
      let role = assessor.role || 'Assessor';

       if (role.toLowerCase() === 'la') {
        role = 'Lead Assessor';
      } else if (role.toLowerCase() === 'lata') {
        role = 'Lead And Technical Assessor';
      } else {
        role = 'Technical Assessor';
      }

      const formatSchedule = (schedule) => {
        if (Array.isArray(schedule) && schedule.length > 0) {
          return schedule.join('\n');
        }
        return schedule || '-';
      };

      return [
        { content: `${role}\n${assessor.name || '-'}\n${assessor.discipline || '-'}\n${
        (formData.field === 'Testing' || formData.field === 'Calibration')
        ? assessor.group ? `Group(s): ${assessor.group || '-'}`: ''
        : ''
        }`, styles: { fontStyle: 'bold', fillColor: index === 0 ? [224, 231, 255] : null } },
        formatSchedule(assessor.day1Morning),
        formatSchedule(assessor.day1Afternoon),
        formatSchedule(assessor.day2Morning),
        formatSchedule(assessor.day2Afternoon)
      ];
    });

    // Assessor Schedule Table
    autoTable(doc, {
      startY: yPos,
      theme: 'grid',
      headStyles: {
        fillColor: [99, 102, 241], // Indigo (#6366f1)
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
        cellPadding: 3
      },
      bodyStyles: {
        fontSize: 8.5,
        cellPadding: 4,
        lineColor: [229, 231, 235], // Gray border (#e5e7eb)
        lineWidth: 0.1,
        minCellHeight: 15
      },
      head: [
        [
          { content: 'Assessor Name & Role', rowSpan: 2, styles: { valign: 'middle' } },
          { content: 'Day 1', colSpan: 2 },
          { content: 'Day 2', colSpan: 2 }
        ],
        ['Morning', 'Afternoon', 'Morning', 'Afternoon']
      ],
      body: assessorRows,
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { cellWidth: 50, valign: 'middle' },
        1: { cellWidth: 35, halign: 'left', valign: 'top' },
        2: { cellWidth: 35, halign: 'left', valign: 'top' },
        3: { cellWidth: 35, halign: 'left', valign: 'top' },
        4: { cellWidth: 35, halign: 'left', valign: 'top' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Save or return the PDF
    const fileName = `NAF1_${new Date().toISOString().split('T')[0]}.pdf`;

    if (returnBlob) {
      const pdfBlob = doc.output('blob');
      return { blob: pdfBlob, fileName };
    } else {
      doc.save(fileName);
    }
  };

  const handlePublish = async () => {
    if (!assessorId || !assessmentId || !appId) {
      showSnackbar('Missing required parameters', 'error');
      return;
    }

    if (!formData.laboratoryName || !formData.visitDates) {
      showSnackbar('Please fill all required fields before publishing', 'warning');
      return;
    }

    if (!formData.declarationSigned) {
      showSnackbar('Please agree to the declaration before publishing', 'warning');
      return;
    }

    setPublishLoading(true);
    try {
      // Generate PDF blob
      console.log('📄 Generating PDF...');
      const { blob, fileName } = generatePDF(true);

      // Convert blob to File object for S3 upload
      const pdfFile = new File([blob], fileName, { type: 'application/pdf' });

      // Upload to S3
      console.log('☁️ Uploading PDF to S3...');
      const s3Response = await s3Utils.uploadFile(pdfFile);

      if (!s3Response.success) {
        throw new Error('Failed to upload PDF to S3');
      }

      const documentUrl = s3Response.url;
      console.log('✅ PDF uploaded to S3:', documentUrl);

      // Call UploadNAF1 API
      const payload = {
        naF1file: documentUrl,
        appid: String(appId),
        assessmentid: String(assessmentId),
      };
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UploadNAF1`;
      const response = await axios.put(url, payload);
      if (response.data.success || response.data.isstatus) {
        console.log('✅ NAF-1 uploaded successfully');

        // Call Email API to send NAF-1 to DO, Lab Head, and Assessors
        try {
          console.log('📧 Sending NAF-1 email to team...');
          const emailPayload = {
            appId: String(appId),
            orgId: String(orgId),
            naf1: documentUrl
          };
          const emailUrl = `${process.env.REACT_APP_API_URL}/assessment-mail/send-email-all-team-do-lab-details`;
          const emailResponse = await axios.post(emailUrl, emailPayload);

          if (emailResponse.data.success) {
            console.log('✅ Email sent successfully to:', {
              doEmail: emailResponse.data.data.recipients.doEmail,
              labEmail: emailResponse.data.data.recipients.labEmail,
              assessorCount: emailResponse.data.data.recipientCount - 2
            });
            showSnackbar("NAF-1 published and uploaded successfully!", "success");
          } else {
            console.warn('⚠️ Email sending failed:', emailResponse.data.message);
            showSnackbar("NAF-1 published and uploaded successfully failed!", "warning");
          }
        } catch (emailError) {
          console.warn('⚠️ Error sending email:', emailError.message);
        }

        setTimeout(() => {
          window.close();
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to upload NAF-1");
      }
    } catch (error) {
      console.error('❌ Error publishing NAF-1:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Error publishing NAF-1',
        'error'
      );
    } finally {
      setPublishLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="NABL | NAF-1" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <DescriptionIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                Assessment Schedule
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Main Form */}
        <form onSubmit={handleSubmit}>
          {/* Laboratory Information */}
          <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 3 }}
              >
                <BusinessIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Laboratory Information
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Name of the Laboratory"
                    value={formData.laboratoryName}
                    onChange={(e) =>
                      handleInputChange("laboratoryName", e.target.value)
                    }
                    variant="outlined"
                    size="medium"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Authorized Representative"
                    value={formData.authorizedRepresentative}
                    onChange={(e) =>
                      handleInputChange(
                        "authorizedRepresentative",
                        e.target.value,
                      )
                    }
                    variant="outlined"
                    size="medium"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Date(s) of Visit"
                    value={formData.visitDates}
                    onChange={(e) =>
                      handleInputChange("visitDates", e.target.value)
                    }
                    placeholder="e.g., 15-16 March 2024"
                    variant="outlined"
                    size="medium"
                    disabled={true}
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: "action.active" }} />
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Assessment Details & Standards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%", boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 3 }}
                  >
                    <AssessmentIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Assessment Details
                    </Typography>
                  </Stack>

                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Assessment Type"
                      value={formData.assessmentType}
                      onChange={(e) =>
                        handleInputChange("assessmentType", e.target.value)
                      }
                      variant="outlined"
                      disabled={true}
                    />

                    <TextField
                      fullWidth
                      label="Discipline(s)"
                      value={formData.discipline}
                      onChange={(e) =>
                        handleInputChange("discipline", e.target.value)
                      }
                      variant="outlined"
                      disabled={true}
                    />
                    {(formData.field === "Testing" ||
                      formData.field === "Calibration") && (
                      <TextField
                        fullWidth
                        label="Group(s)"
                        value={formData.group}
                        onChange={(e) =>
                          handleInputChange("group", e.target.value)
                        }
                        variant="outlined"
                        disabled={true}
                      />
                    )}
                    <TextField
                      fullWidth
                      label="Field"
                      value={formData.field}
                      onChange={(e) =>
                        handleInputChange("field", e.target.value)
                      }
                      variant="outlined"
                      disabled={true}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%", boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 3 }}
                  >
                    <CheckCircleIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Assessment Standards
                    </Typography>
                  </Stack>

                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Assessment Standard"
                      value={formData.assessmentStandard}
                      onChange={(e) =>
                        handleInputChange("assessmentStandard", e.target.value)
                      }
                      variant="outlined"
                      disabled={true}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Assessment Timings */}
          <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 3 }}
              >
                <TimeIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Assessment Schedule & Timings
                </Typography>
              </Stack>

              <Grid container spacing={4}>
                {/* Daily Timings */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
                    >
                      Daily Assessment Timings
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={12}>
                        <TextField
                          fullWidth
                          label="Morning"
                          value={formData.morningStart}
                          onChange={(e) =>
                            handleInputChange("morningStart", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6} md={12}>
                        <TextField
                          fullWidth
                          label="Afternoon"
                          value={formData.afternoonStart}
                          onChange={(e) =>
                            handleInputChange("afternoonStart", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Meetings */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
                    >
                      Meeting Schedule
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Opening Meeting
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} md={12}>
                            <TextField
                              fullWidth
                              size="small"
                              type="datetime-local"
                              value={formData.openingMeetingDate}
                              onChange={(e) =>
                                handleInputChange(
                                  "openingMeetingDate",
                                  e.target.value,
                                )
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Closing Meeting
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} md={12}>
                            <TextField
                              fullWidth
                              size="small"
                              type="datetime-local"
                              value={formData.closingMeetingDate}
                              onChange={(e) =>
                                handleInputChange(
                                  "closingMeetingDate",
                                  e.target.value,
                                )
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* Debriefings */}
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: "text.secondary" }}
                >
                  Daily Debriefings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Day 1 Debriefing
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6} md={12}>
                          <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            value={formData.day1DebriefingDate}
                            onChange={(e) =>
                              handleInputChange(
                                "day1DebriefingDate",
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Day 2 Debriefing
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6} md={12}>
                          <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            value={formData.day2DebriefingDate}
                            onChange={(e) =>
                              handleInputChange(
                                "day2DebriefingDate",
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
          {/* Submit and Publish Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                minWidth: 200,
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                  {assessmentScheduleId ? "Updating..." : "Submitting..."}
                </>
              ) : assessmentScheduleId ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Box>

          {/* Assessor Schedule */}
          <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Assessor Schedule
                  </Typography>
                  {loadingAssessors && (
                    <CircularProgress size={20} sx={{ ml: 2 }} />
                  )}
                </Stack>
              </Stack>

              {assessors.length === 0 && !loadingAssessors ? (
                <Alert severity="info">
                  No assessors assigned for this assessment.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                      height: 8,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "grey.200",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "primary.main",
                      borderRadius: 4,
                    },
                  }}
                >
                  <Table sx={{ minWidth: { xs: 800, md: "auto" } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            bgcolor: "primary.light",
                            color: "primary.contrastText",
                            minWidth: 150,
                            position: { xs: "sticky", md: "static" },
                            left: 0,
                            zIndex: 3,
                          }}
                        >
                          Assessor Details
                        </TableCell>
                        <TableCell
                          colSpan={2}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            bgcolor: "secondary.light",
                            color: "secondary.contrastText",
                            minWidth: 300,
                          }}
                        >
                          Day 1 Schedule
                        </TableCell>
                        <TableCell
                          colSpan={2}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            bgcolor: "secondary.light",
                            color: "secondary.contrastText",
                            minWidth: 300,
                          }}
                        >
                          Day 2 Schedule
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.50",
                            minWidth: 150,
                            position: { xs: "sticky", md: "static" },
                            left: 0,
                            zIndex: 2,
                          }}
                        >
                          Name & Expertise
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.50",
                            textAlign: "center",
                            minWidth: 150,
                          }}
                        >
                          Morning
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.50",
                            textAlign: "center",
                            minWidth: 150,
                          }}
                        >
                          Afternoon
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.50",
                            textAlign: "center",
                            minWidth: 150,
                          }}
                        >
                          Morning
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.50",
                            textAlign: "center",
                            minWidth: 150,
                          }}
                        >
                          Afternoon
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assessors.map((assessor, index) => (
                        <TableRow key={assessor.id} hover>
                          <TableCell
                            sx={{
                              position: { xs: "sticky", md: "static" },
                              left: 0,
                              zIndex: 1,
                              bgcolor: "background.paper",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {assessor.name || "-"}
                                <br />
                                <Box
                                  component="span"
                                  sx={{ color: "#2563eb", fontWeight: 600 }}
                                >
                                  {assessor.discipline
                                    ? `(${assessor.discipline})`
                                    : "-"}
                                </Box>
                                {(formData.field==='Testing' || formData.field==='Calibration') &&(
                                  <>
                                <br />
                                <Box
                                  component="span"
                                  sx={{ color: "#16a34a", fontWeight: 600 }}
                                >
                                  {assessor.group ? `(${assessor.group})` : "-"}
                                </Box>
                                </>
                                )}
                              </Typography>
                              <Chip
                                label={assessor.role}
                                size="small"
                                color={index === 0 ? "primary" : "default"}
                                sx={{ mb: 1, fontWeight: 600 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Select
                              fullWidth
                              multiple
                              size="small"
                              value={
                                Array.isArray(assessor.day1Morning)
                                  ? assessor.day1Morning
                                  : []
                              }
                              onChange={(e) =>
                                updateAssessorSchedule(
                                  index,
                                  "day1Morning",
                                  e.target.value,
                                )
                              }
                              input={<OutlinedInput />}
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              }}
                            >
                              {(labTypeId === '3'? SCHEDULE_OPTIONS : SCHEDULE_OPTIONS_TESTING_CALIBRATION).map((option) => (
                                <MenuItem key={option} value={option}>
                                  <Checkbox
                                    checked={
                                      (assessor.day1Morning || []).indexOf(
                                        option,
                                      ) > -1
                                    }
                                  />
                                  <ListItemText primary={option} />
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              fullWidth
                              multiple
                              size="small"
                              value={
                                Array.isArray(assessor.day1Afternoon)
                                  ? assessor.day1Afternoon
                                  : []
                              }
                              onChange={(e) =>
                                updateAssessorSchedule(
                                  index,
                                  "day1Afternoon",
                                  e.target.value,
                                )
                              }
                              input={<OutlinedInput />}
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              }}
                            >
                              {(labTypeId === '3' ? SCHEDULE_OPTIONS : SCHEDULE_OPTIONS_TESTING_CALIBRATION).map((option) => (
                                <MenuItem key={option} value={option}>
                                  <Checkbox
                                    checked={
                                      (assessor.day1Afternoon || []).indexOf(
                                        option,
                                      ) > -1
                                    }
                                  />
                                  <ListItemText primary={option} />
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              fullWidth
                              multiple
                              size="small"
                              value={
                                Array.isArray(assessor.day2Morning)
                                  ? assessor.day2Morning
                                  : []
                              }
                              onChange={(e) =>
                                updateAssessorSchedule(
                                  index,
                                  "day2Morning",
                                  e.target.value,
                                )
                              }
                              input={<OutlinedInput />}
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              }}
                            >
                              {(labTypeId === '3' ? SCHEDULE_OPTIONS : SCHEDULE_OPTIONS_TESTING_CALIBRATION).map((option) => (
                                <MenuItem key={option} value={option}>
                                  <Checkbox
                                    checked={
                                      (assessor.day2Morning || []).indexOf(
                                        option,
                                      ) > -1
                                    }
                                  />
                                  <ListItemText primary={option} />
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              fullWidth
                              multiple
                              size="small"
                              value={
                                Array.isArray(assessor.day2Afternoon)
                                  ? assessor.day2Afternoon
                                  : []
                              }
                              onChange={(e) =>
                                updateAssessorSchedule(
                                  index,
                                  "day2Afternoon",
                                  e.target.value,
                                )
                              }
                              input={<OutlinedInput />}
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              }}
                            >
                              {(labTypeId === '3' ? SCHEDULE_OPTIONS : SCHEDULE_OPTIONS_TESTING_CALIBRATION).map((option) => (
                                <MenuItem key={option} value={option}>
                                  <Checkbox
                                    checked={
                                      (assessor.day2Afternoon || []).indexOf(
                                        option,
                                      ) > -1
                                    }
                                  />
                                  <ListItemText primary={option} />
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {assessors.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleIcon />}
                    onClick={saveAssessorSchedule}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    {loading ? "Saving..." : "Save Schedule"}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Declaration */}
          <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 3, color: "primary.main" }}
              >
                Declaration
              </Typography>

              <Paper
                variant="outlined"
                sx={{ p: 3, bgcolor: "grey.50", mb: 2 }}
              >
                <Typography variant="body1" sx={{ fontStyle: "italic", mb: 2 }}>
                  "I declare that the assessment schedule has been prepared
                  after discussing with the assessment team members."
                </Typography>

                <FormControlLabel
                  control={
                    <Radio
                      checked={formData.declarationSigned}
                      onChange={(e) =>
                        handleInputChange("declarationSigned", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      I agree to the above declaration
                    </Typography>
                  }
                />
              </Paper>

              <Alert
                severity={formData.declarationSigned ? "success" : "warning"}
              >
                {formData.declarationSigned
                  ? "Declaration accepted. You can now submit the form."
                  : "Please agree to the declaration to submit the form."}
              </Alert>
            </CardContent>
          </Card>

          {/* Submit and Publish Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            {/* Show Publish button only if both assessment schedule ID and assessor schedule ID exist */}
            {assessmentScheduleId &&
              assessmentScheduleId !== "" &&
              assessorScheduleIdExist && (
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={publishLoading || !formData.declarationSigned}
                  onClick={handlePublish}
                  startIcon={<UploadIcon />}
                  sx={{
                    px: 6,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    minWidth: 200,
                  }}
                >
                  {publishLoading ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1 }}
                        color="inherit"
                      />
                      Publishing...
                    </>
                  ) : (
                    "Publish"
                  )}
                </Button>
              )}
          </Box>
        </form>
        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Location Warning Dialog */}
        <LocationWarningDialog
          open={locationWarningOpen}
          onClose={() => setLocationWarningOpen(false)}
          distanceMessage={distanceMessage}
        />

        {/* Hourly Attendance Dialog */}
        <HourlyAttendanceDialog
          open={attendanceCheckDialogOpen}
          onClose={() => setAttendanceCheckDialogOpen(false)}
          message={attendanceCheckMessage}
          onRecheck={checkHourlyAttendance}
        />
      </Container>
    </>
  );
};

export default NAF1Form;